import { Component, EventEmitter, inject, input, Input, OnInit, Output } from '@angular/core';

import { ErrorIconComponent, SpinnerComponent } from 'remarketing-ui-components-lib';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ChangeStatusModalDirective } from '../change-status-modal.directive';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import {
  AuctionService,
  BatchPostingService,
  InventoryManagementAuctionItemStatus,
  LaunchDarklyService,
  ReservationRequestDto,
  ReservationResponseDto,
  ReservationsService
} from 'remarketing-angular-library';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-make-unreserve-modal',
  imports: [SpinnerComponent, ErrorIconComponent, TranslocoDirective],
  templateUrl: './make-unreserve-modal.component.html',
  styleUrl: './make-unreserve-modal.component.scss'
})
export class MakeUnreserveModalComponent extends ChangeStatusModalDirective implements OnInit   {
  public readonly numberOfVehicles$ = input<number>(1);

  protected buyerOeUuid: string;
  protected isLoading: boolean;
  protected isError: boolean;
  protected isIsvFileProcessing = true;
  protected textError: string;
  protected translations = {
    textHeader: '',
    textSubHeader: '',
    textReservationChangesDisabled: ''
  };

  private readonly reservationService: ReservationsService;
  private readonly batchPostingService: BatchPostingService;

  constructor() {
    super();
    this.reservationService = inject(ReservationsService);
    this.batchPostingService = inject(BatchPostingService);
  }

  get isIsvReservationHaltEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-reservationHaltEnabled', false);
  }

  get isIsvFileUploadHaltEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-fileProcessingReservationHaltEnabled', false);
  }

  ngOnInit(): void {
    this.getTranslations();
    this.checkIsvFileProcessingStatus();
  }

  checkIsvFileProcessingStatus(): void {
    if (this.isIsvReservationHaltEnabled) {
      this.isIsvFileProcessing = true;
    } else if (this.isIsvFileUploadHaltEnabled) {
      this.batchPostingService.checkReservationsEnabled().pipe(take(1)).subscribe({
        next: value => {
          this.isIsvFileProcessing = !value.reservationEnabled;
        },
        error: error => {
          console.log(error);
        }
      });
    } else {
      this.isIsvFileProcessing = false;
    }
  }

  makeUnreserve() {
    this.isLoading = true;
    if (this.isIsvReservationHaltEnabled) {
      this.setReservationErrors();
    } else if (this.isIsvFileUploadHaltEnabled) {
      // @TODO: do we need this check if we already know what it is in isIsvFileProcessing?
      this.batchPostingService
        .checkReservationsEnabled()
        .pipe(take(1))
        .subscribe({
          next: value => {
            if (value.reservationEnabled) {
              this.performUnreserve();
            } else {
              this.setReservationErrors();
            }
          }
        });
    } else {
      this.performUnreserve();
    }
  }

  private performUnreserve() {
    const auctionItemUuids = [];
    this.selectedVehicles.forEach(vehicle => {
      // only adding RESERVED vehicles to the request
      if (vehicle.auctionItemStatus === 'RESERVED') {
        auctionItemUuids.push(vehicle.vehicleUuid);
        this.buyerOeUuid = vehicle.reservation.reservedForUuid;
      }
    });

    const payload = {
      auctionItemUuids,
      buyerOeUuid: this.buyerOeUuid,
      reservationStatus: 'UNRESERVE'
    } as ReservationRequestDto;
    this.reservationService
    .postVehicleReservations(payload)
    .pipe(take(1))
    .subscribe((response: ReservationResponseDto[]) => {
      this.changeReserveStatus(InventoryManagementAuctionItemStatus.ACTIVE, response);
      this.isLoading = false;
    });
  }

  setReservationErrors() {
    this.isLoading = false;
    this.isError = true;
    this.textError = this.translations.textReservationChangesDisabled;
  }

  closeModal() {
    this.isLoading = false;
    this.isError = false;
    this.close.emit();
  }

  getTranslations() {
    super.getTranslations();
    const keyParams = {
      'sellModule.manageInventory.actionListModal.makeUnreserveModal.textHeader': {
        numberOfVehicles: this.numberOfVehicles$()
      },
      'sellModule.manageInventory.actionListModal.makeUnreserveModal': {}
    };

    this.translocoService
    .selectTranslateObject(keyParams)
    .pipe(takeUntil(this.destroyed$))
    .subscribe(translate => {
      this.translations.textHeader = translate[0];
      this.translations.textSubHeader = translate[1].textSubHeader;
      this.translations.textReservationChangesDisabled = translate[1].textReservationChangesDisabled;
    });
  }
}
